import { type StoreLocationCategoryType } from '@/services/types/mockStoreLocationCategories'
import { type StoreLocation } from '@/services/types/mockStoreLocations'
import {
  type PhotoGalleryItemProps,
  type StoreLocationDetailsType,
} from '@/services/types/storeLocationDetails/mockStoreLocationDetails'
import { type ProviderCategoryListResponse } from '@/services/types/providerCategoryList'
import { type ServiceResponseType } from '@/utils/types/ServiceResponseType'
import { type Key } from 'react'
import { api } from './api'
import { endpoints } from './endpoints'

export type CreateStoreLocationProps = Pick<
  StoreLocationDetailsType,
  | 'title'
  | 'streetAddress'
  | 'streetAddressDetail'
  | 'addressLocality'
  | 'addressRegion'
  | 'postalCode'
  | 'hideAddressInListing'
  | 'country'
  | 'languageCode'
  | 'primaryPhoneCountryCode'
  | 'primaryPhoneNumber'
> & {
  categories: Key[]
}

export type UpdateStoreLocationProps = Omit<StoreLocationDetailsType, 'categories' | 'media'> & {
  categories: Key[]
  photoGallery: Partial<PhotoGalleryItemProps>[]
}

interface BulkUpdateProps {
  ids: string[]
  folderIds: string[]
}

export type BulkUpdateStoreLocationsProps = BulkUpdateProps & Partial<UpdateStoreLocationProps>

export interface StoreLocationsServiceProps {
  createStoreLocation: (data: CreateStoreLocationProps) => ServiceResponseType<StoreLocation>
  deleteStoreLocationById: (id: string) => ServiceResponseType<void>
  deleteStoreLocations: (ids: string[]) => ServiceResponseType<void>
  getStoreLocationById: (id: string) => ServiceResponseType<StoreLocationDetailsType>
  getStoreLocationCategories: () => ServiceResponseType<StoreLocationCategoryType[]>
  getProviderCategoryList: (id: string) => ServiceResponseType<ProviderCategoryListResponse>
  getStoreLocations: (folderIds?: string[]) => ServiceResponseType<StoreLocation[]>
  updateStoreLocation: (
    id: string,
    data: Partial<UpdateStoreLocationProps>,
  ) => ServiceResponseType<StoreLocationDetailsType>
  bulkUpdateStoreLocations: (data: BulkUpdateStoreLocationsProps) => ServiceResponseType<void> // TODO: Change response types when error handler be improved
}

export const storeLocationsService: StoreLocationsServiceProps = {
  async createStoreLocation(data: CreateStoreLocationProps) {
    return api.post<StoreLocation>(endpoints.storeLocations, data)
  },

  async deleteStoreLocationById(id) {
    return api.delete(`${endpoints.storeLocations}/${id}`)
  },

  async deleteStoreLocations(ids) {
    return api.post(`${endpoints.storeLocations}/delete`, { idsToDelete: ids })
  },

  async getStoreLocationById(id) {
    return api.get<StoreLocationDetailsType>(`${endpoints.storeLocations}/${id}`)
  },

  async getStoreLocationCategories() {
    return api.get<StoreLocationCategoryType[]>(endpoints.storeLocationCategories)
  },

  async getProviderCategoryList(id: string) {
    return api.get<ProviderCategoryListResponse>(
      `${endpoints.storeLocations}/${id}/provider-category-list`,
    )
  },

  async getStoreLocations(folderIds?: string[]) {
    return api.get<StoreLocation[]>(endpoints.storeLocations, {
      params: folderIds?.length ? { folderIds } : undefined,
    })
  },

  async updateStoreLocation(id: string, data: Partial<UpdateStoreLocationProps>) {
    return api.patch<StoreLocationDetailsType>(`${endpoints.storeLocations}/${id}`, data)
  },

  async bulkUpdateStoreLocations(data: BulkUpdateStoreLocationsProps) {
    return api.patch(`${endpoints.storeLocations}/bulk-update`, data)
  },
}
